import * as THREE from "three";
import { WallType, WallConnectionStart, WallConnectionEnd, isWallType } from "../types/floorplannerTypes";
import { getArcTangentAtEndpoint } from "./arcUtils";

/**
 * Cross-product threshold for treating a neighbour wall as "turning" away
 * from the chord direction. Anything below this is considered collinear and
 * contributes no vote to either side.
 */
const CP_THRESHOLD = 0.01;

type EndpointKey = "start" | "end";

/**
 * Resolver used by {@link computeInnerMeasurementSide} to look up the wall
 * referenced by a connection. Decoupling this from any specific store lets us
 * unit-test the geometric logic without constructing the full MobX store.
 */
export type WallResolver = (wallId: string) => WallType | undefined | null;

/**
 * Determines which side of a wall (left/right relative to the wall's forward
 * direction) corresponds to the architectural "inner" (concave) side, based
 * on the geometry of its endpoint connections.
 *
 * For each endpoint connection we compute the cross product between this
 * wall's forward direction (the Bezier tangent at the endpoint for arc
 * walls) and the connected wall's "away-from-corner" direction. A positive
 * cross product means the neighbour turns to the left of the wall, which
 * makes the LEFT side concave; a negative cross product means the RIGHT
 * side is concave.
 *
 * Middle connections are ignored because they don't define a corner at an
 * endpoint.
 *
 * Returns null when no endpoint connection clearly indicates an inner side
 * (unconnected wall, middle-only connections, or endpoint connections that
 * cancel each other out). Callers should fall back to a generic outline
 * strategy in that case.
 */
export const computeInnerMeasurementSide = (
    wall: WallType,
    resolveWall: WallResolver,
): "left" | "right" | null => {
    if (!wall || !isWallType(wall)) return null;

    const wallLength = wall.start.distanceTo(wall.end);
    if (wallLength < 1e-6) return null;

    let leftInnerScore = 0;
    let rightInnerScore = 0;

    const wallForwardAtEndpoint = (endpoint: EndpointKey): THREE.Vector2 => {
        if (wall.lineForm === "arc" && wall.controlPoint) {
            return getArcTangentAtEndpoint(wall, endpoint).clone();
        }
        return new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
    };

    const scoreConnection = (
        sourceEndpoint: EndpointKey,
        otherWallId: string,
        targetEndpointValue: "start" | "end" | "middle" | number | undefined,
    ) => {
        // Middle connections don't form an endpoint corner on this wall.
        if (targetEndpointValue === "middle") return;

        const otherWall = resolveWall(otherWallId);
        if (!otherWall || !isWallType(otherWall)) return;

        const otherEndpoint: EndpointKey | null =
            targetEndpointValue === "start" || targetEndpointValue === WallConnectionStart
                ? "start"
                : targetEndpointValue === "end" || targetEndpointValue === WallConnectionEnd
                    ? "end"
                    : null;
        if (!otherEndpoint) return;

        // Neighbour wall's direction pointing AWAY from the shared corner.
        let otherAway: THREE.Vector2;
        if (otherWall.lineForm === "arc" && otherWall.controlPoint) {
            const tangent = getArcTangentAtEndpoint(otherWall, otherEndpoint);
            // The tangent points in the neighbour's forward direction: away
            // from the corner when the connection is at its "start", toward
            // the corner when the connection is at its "end" - flip in that
            // case to consistently represent the away-from-corner direction.
            otherAway = otherEndpoint === "start" ? tangent.clone() : tangent.clone().negate();
        } else {
            otherAway = otherEndpoint === "start"
                ? new THREE.Vector2().subVectors(otherWall.end, otherWall.start)
                : new THREE.Vector2().subVectors(otherWall.start, otherWall.end);
        }

        if (otherAway.lengthSq() < 1e-12) return;
        otherAway.normalize();

        const wallForward = wallForwardAtEndpoint(sourceEndpoint);
        if (wallForward.lengthSq() < 1e-12) return;

        const cp = wallForward.x * otherAway.y - wallForward.y * otherAway.x;
        if (cp > CP_THRESHOLD) leftInnerScore++;
        else if (cp < -CP_THRESHOLD) rightInnerScore++;
    };

    wall.centerConnections?.forEach(conn => {
        const sourceEndpoint: EndpointKey | null =
            conn.sourcePosition === WallConnectionStart
                ? "start"
                : conn.sourcePosition === WallConnectionEnd
                    ? "end"
                    : null;
        if (!sourceEndpoint) return;
        scoreConnection(sourceEndpoint, conn.id, conn.targetPosition as any);
    });

    wall.edgeConnections?.forEach(conn => {
        if (conn.sourceEndpoint !== "start" && conn.sourceEndpoint !== "end") return;
        scoreConnection(conn.sourceEndpoint, conn.targetWallId, conn.targetEndpoint);
    });

    if (leftInnerScore > rightInnerScore) return "left";
    if (rightInnerScore > leftInnerScore) return "right";
    return null;
};
